<script setup>
import { load } from "cheerio";
import {
	ReferencedStocks,
	ReferencedStocksSidebar
} from "@stockstotrade_com/stt-ts-shared-lib";
import {
	extractStockSymbol,
	processHeadingsForReferencedStocks,
	STOCK_TICKER_REGEX
} from "~/utils/referenced-stocks-content";
import { useStocksStore } from "~/stores/stocks";
const stocksStore = useStocksStore();

const $route = useRoute();
const $config = useRuntimeConfig();
const widgetContent = ref();
const hasTwitterEmbedd = ref(false);
const mentioned_above_urls = ref([]);
const stockSymbols = ref([]);
const faqItems = ref([]);
let title = "";
const showAds = ref(false);
const beyondwords = ref();
const aizenSidebarAd = useState(
	"aizen-ad-c26b2507-1c06-43a3-bf84-32efaf76127e",
	() => null
);

const { current_hostname } = useRuntimeConfig().public;

definePageMeta({
	key: (route) => route.fullPath
});

const pennyStockingChapters = [
	"/penny-stocks/",
	"/penny-stocking-chapter-01/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-2/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-3/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-4/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-5/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-6/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-7/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-8/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-9/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-10/",
	"/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-11/"
];

const isPennyStockChapter = ref(false);

store.toggleLoading();


const schemaContentParser = (content) => {
	const imageList = [];
	const $ = load(content, {
		decodeEntities: false // Prevent HTML entity decoding
	});

	$("img").each((_index, el) => {
		imageList.push({
			"@type": "ImageObject",
			url: el.attribs.src,
			width: el.attribs.width,
			height: el.attribs.height
		});
	});

	$('[data-beyondwords-player="true"], script, style, img').remove();
	// Extract text content
	const textContent = $("body").text();

	return { textContent, imageList };
};

const normalizeSchemaDate = (dateString) => {
	if (!dateString) return dateString;

	if (dateString.endsWith("Z")) {
		return dateString;
	}

	if (/[+-]\d{2}:\d{2}$/.test(dateString)) {
		return dateString;
	}

	return `${dateString}Z`;
};

const schemaMaker = (post, current_hostname, authors) => {
	const payload = [];
	const article = {
		"@context": "https://schema.org",
		"@id": current_hostname + "blog/" + post.slug + "/#Article",
		url: current_hostname + "blog/" + post.slug + "/",
		inLanguage: "en-US",
		mainEntityOfPage: current_hostname + "blog/" + post.slug + "/",
		headline: post.title,
		description: post.yoast_head_json.description
	};

	const author = authors.find(
		(author) =>
			author.name === post.author.name ||
			(author.name === "Tim Sykes" && post.author.name === "Timothy Sykes") ||
			(post.author.name === "dzi11" && author.name === "Tim Sykes")
	);

	let social_links;

	if (author && author.custom_sections) {
		author.custom_sections = isValidJSON(author.custom_sections)
			? JSON.parse(author.custom_sections)
			: author.custom_sections;

		social_links =
			author &&
			author.custom_sections
				.find((section) => section.name === "Social")
				?.values.map((item) => item.value);
	}

	const reviewers = [];

	const reviewed_by = authors.find(
		(author) =>
			author.name === post.reviewed_by ||
			(author.name === "Tim Sykes" && post.reviewed_by === "Timothy Sykes")
	);

	const fact_checked_by = authors.find(
		(author) =>
			author.name === post.fact_checked_by ||
			(author.name === "Tim Sykes" && post.fact_checked_by === "Timothy Sykes")
	);

	if (reviewed_by) {
		reviewed_by.custom_sections = isValidJSON(reviewed_by.custom_sections)
			? JSON.parse(reviewed_by.custom_sections)
			: reviewed_by.custom_sections;
		reviewed_by.social_links = reviewed_by.custom_sections
			.find((section) => section.name === "Social")
			?.values.map((item) => item.value);

		reviewers.push({
			"@type": "Person",
			name: reviewed_by.name,
			url:
				current_hostname +
				"blog/author/" +
				reviewed_by.name.replace(/ /g, "-").toLowerCase() +
				"/",
			sameAs: reviewed_by.social_links.find(
				(item) =>
					item.includes("twitter") ||
					item.includes("Twitter") ||
					item.includes("x") ||
					item.includes("X")
			)
		});
	}

	if (fact_checked_by) {
		fact_checked_by.custom_sections = isValidJSON(fact_checked_by.custom_sections)
			? JSON.parse(fact_checked_by.custom_sections)
			: fact_checked_by.custom_sections;
		fact_checked_by.social_links = fact_checked_by.custom_sections
			.find((section) => section.name === "Social")
			?.values.map((item) => item.value);

		reviewers.push({
			"@type": "Person",
			name: fact_checked_by.name,
			url:
				current_hostname +
				"blog/author/" +
				fact_checked_by.name.replace(/ /g, "-").toLowerCase() +
				"/",
			sameAs: fact_checked_by.social_links.find(
				(item) =>
					item.includes("twitter") ||
					item.includes("Twitter") ||
					item.includes("x") ||
					item.includes("X")
			)
		});
	}

	// Pages get their `_embedded["wp:term"][0]` populated with the page's
	// assigned tags (taxonomy: "post_tag"), not real categories. Only emit
	// the breadcrumb's "category" entry when the term is actually a category.
	const breadcrumbCategory = Array.isArray(post.categories)
		? post.categories.find((c) => c && c.taxonomy === "category")
		: null;

	if (breadcrumbCategory) {
		const breadcrumb_list = {
			"@type": "BreadcrumbList",
			"@id": current_hostname + "blog/" + post.slug + "/#breadcrumb",
			"@context": "https://schema.org",
			itemListElement: [
				{
					"@type": "ListItem",
					position: 1,
					item: {
						"@id": current_hostname,
						name: "Timothy Sykes"
					}
				},
				{
					"@type": "ListItem",
					position: 2,
					item: {
						"@id": current_hostname + "category/" + breadcrumbCategory.slug + "/",
						name: breadcrumbCategory.name
					}
				},
				{
					"@type": "ListItem",
					position: 3,
					item: {
						"@id": current_hostname + "blog/" + post.slug + "/",
						name: post.title
					}
				}
			]
		};

		payload.push(breadcrumb_list);
	}

	const featuredImage = {
		"@type": "ImageObject",
		"@id": current_hostname + "blog/" + post.slug + "/#primaryimage",
		url:
			post._embedded["wp:featuredmedia"] && post._embedded["wp:featuredmedia"][0]
				? post._embedded["wp:featuredmedia"][0].source_url
				: "",
		width: "740",
		height: "532"
	};

	const parsedStuff = schemaContentParser(post.content);

	// WP pages often store assigned tags under categories (taxonomy: post_tag).
	const keywordTags = [
		...(Array.isArray(post.tags) ? post.tags : []),
		...(Array.isArray(post.categories)
			? post.categories.filter((c) => c && c.taxonomy === "post_tag")
			: [])
	];

	if (keywordTags.length > 0) {
		article.keywords = keywordTags[0].name + ",";
	}

	let schema_parse = null;
	let datesFromSchema = null;

	if (post.yoast_head_json?.schema_json) {
		try {
			schema_parse = JSON.parse(post.yoast_head_json.schema_json);
			datesFromSchema = schema_parse["@graph"]?.find((item) => {
				const type = Array.isArray(item?.["@type"])
					? item["@type"][0]
					: item?.["@type"];
				return type === "Article" || type === "WebPage";
			});
		} catch {
			schema_parse = null;
		}
	}

	if (!article.keywords && datesFromSchema?.keywords) {
		article.keywords = datesFromSchema.keywords;
	}

	const primaryImageFromSchema = schema_parse?.["@graph"]?.find(
		(item) =>
			item["@type"] === "ImageObject" && item["@id"]?.includes("#primaryimage")
	);

	if (primaryImageFromSchema) {
		featuredImage.url =
			primaryImageFromSchema.contentUrl ||
			primaryImageFromSchema.url ||
			featuredImage.url;
		featuredImage.width = primaryImageFromSchema.width || featuredImage.width;
		featuredImage.height = primaryImageFromSchema.height || featuredImage.height;
	}

	const timAuthor = {
		"@type": "Person",
		name: "Timothy Sykes",
		url: current_hostname + "blog/author/timothysykes/",
		description: "",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};
	const mattMonaco = {
		"@type": "Person",
		name: "Matt Monaco",
		url: current_hostname + "blog/author/mattmonaco/",
		description: "",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/f47e982e112566f6e63ad14aa64d1829?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};

	const timBot = {
		"@type": "Person",
		name: "Tim bot",
		url: current_hostname + "blog/author/tim-bot/",
		image: {
			"@type": "ImageObject",
			url: "https://secure.gravatar.com/avatar/341a364cbdd97f47f09948e5ca2e682e?s=96&d=mm&r=g",
			height: 96,
			width: 96
		}
	};

	const printEditor = (author = 1) => {
		if (author === 1) {
			return {
				...timAuthor,
				description: "",
				sameAs: [current_hostname + "/blog/author/timothysykes/", "timothysykes"]
			};
		}

		if (author === 6500) {
			return {
				...timBot
				// sameAs: [current_hostname + "/blog/author/tim-bot/", "tim-bot"]
			};
		}
		if (author === 6486) {
			return {
				...mattMonaco
				// sameAs: [current_hostname + "/blog/author/tim-bot/", "tim-bot"]
			};
		}

		return {
			...timAuthor,
			description: "",
			sameAs: [current_hostname + "/blog/author/timothysykes/", "timothysykes"]
		};
	};

	const publisher = {
		"@type": "Organization",
		name: "Timothy Sykes",
		url: current_hostname,
		image: {
			"@type": "ImageObject",
			url: "https://content.timothysykes.com/wp-content/uploads/2020/10/timothy-logo-160-50.png",
			height: 160,
			width: 50
		}
	};

	const postDate = [
		datesFromSchema?.datePublished,
		post.date_gmt,
		post.date
	].find((v) => v && typeof v === "string");

	const postModified = [
		datesFromSchema?.dateModified,
		post.modified_gmt,
		post.modified,
		postDate
	].find((v) => v && typeof v === "string");

	if (postDate) article.datePublished = normalizeSchemaDate(postDate);
	if (postModified) article.dateModified = normalizeSchemaDate(postModified);
	article.author = {
		"@type": "Person",
		name: author?.name || "Timothy Sykes",
		url: `https://www.timothysykes.com/blog/author/${
			author && author.name
				? author.name.toLowerCase().replace(/ /g, "-")
				: "tim-sykes"
		}/`,
		sameAs: social_links || [
			"https://www.facebook.com/timsykesfans/",
			"https://twitter.com/timothysykes",
			"https://www.linkedin.com/in/timothysykes/",
			"https://www.instagram.com/timothysykes/",
			"https://www.youtube.com/channel/UCoSG43KFjTe0trsteSQ46Ng"
		],
		image: [
			{
				"@type": "ImageObject",
				url:
					author?.profile_image ||
					"https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp",
				width: 306,
				height: 384
			},
			{
				"@type": "ImageObject",
				url:
					author?.profile_image ||
					"https://secure.gravatar.com/avatar/092522e87ab76a4bb688d6bfdc0b4f2e?s=96&d=mm&r=g",
				height: 96,
				width: 96
			}
		],
		jobTitle: "Penny Stock Trader and Mentor",
		publishingPrinciples: "https://www.timothysykes.com/editorial-guidelines/",
		affiliation: {
			"@type": "Organization",
			name: "Timothy Sykes",
			sameAs: "https://www.timothysykes.com"
		}
	};
	article.editor = printEditor(post.author);
	article.publisher = publisher;

	if (post.featured_media) {
		article.image = [featuredImage, ...parsedStuff.imageList];
	} else {
		article.image = [...parsedStuff.imageList];
	}

	payload.push({ "@type": "Article", ...article });

	if (
		post.categories &&
		post.categories.length &&
		[118, 3].includes(post.categories[0].id)
	) {
		const youtubeStuff = getYoutubeLink(post.content);
		const videoObject = {
			"@context": "https://schema.org",
			"@type": "VideoObject",
			"@id": current_hostname + "blog/" + post.slug + "/#videoobject",
			url: current_hostname + "blog/" + post.slug + "/",
			headline: post.title,
			datePublished: article.datePublished,
			dateModified: article.dateModified,
			description: post.yoast_head_json.description,
			transcript: parsedStuff.textContent,
			name: post.title,
			uploadDate: article.datePublished,
			thumbnailUrl: youtubeStuff ? youtubeStuff.thumbnailUrl : "",
			contentUrl: youtubeStuff ? youtubeStuff.contentUrl : "",
			embedUrl: youtubeStuff ? youtubeStuff.embedUrl : "",
			author: timAuthor,
			publisher,
			image: [featuredImage, ...parsedStuff.imageList]
		};

		payload.push(videoObject);
	}

	return [
		{
			"@context": "https://schema.org"
		},
		...payload
	];
};
const { data, error: asyncError } = await useAsyncData(
	`widget_page_${$route.path}`,
	async ({ $config, $router }) => {
		const payload = {
			post: null,
			metadata: null,
			schema_json: null
		};

		const params = $router.currentRoute._rawValue.params;
		const route = $router.currentRoute._rawValue;

		if (route.name !== "widgetpage" && params.slug) {
			window.location.href = "/blog/" + params.slug[0] + "/";
			return { redirected: true, url: params.slug[0] };
		}

		const routePath = $router.currentRoute._rawValue.path;
		const SLUG = $router.currentRoute._rawValue.params.widgetpage[0];

		if (
			routePath.startsWith("/.well-known") ||
			routePath.includes("favicon.ico")
		) {
			console.warn("Ignoring internal route for data fetch:", routePath);
			return payload;
		}

		// Per-slug payload comes straight from S3 via /api/dictionary/pages
		// (ts-posts/pages/<slug>.json). If not found as a WP Page, we fall back
		// to /api/dictionary/posts — some root-level "answer pages" (e.g.
		// /best-quantum-computing-stocks) are actually WP Posts whose canonical
		// WP URL is /blog/<slug>/ but which the frontend deliberately serves at
		// a top-level path. Both endpoints self-heal (live WP lookup + Songbird
		// store) when the S3 entry doesn't exist yet.
		let page_data;
		try {
			page_data = await $fetch(`/api/dictionary/pages?slug=${SLUG}`);
		} catch (pageErr) {
			if (pageErr?.statusCode !== 404) {
				console.warn("[widget page SSR] pages dictionary lookup failed:", pageErr?.message);
			}
			// Not a WP Page — try Posts before giving up.
			try {
				page_data = await $fetch(`/api/dictionary/posts?slug=${SLUG}`);
			} catch (postErr) {
				throw createError({
					statusCode: 404,
					statusMessage: "Page Not Found",
					fatal: true
				});
			}
		}

		try {
			// Some legacy S3 page entries are still the lightweight index stub
			// (no content, yoast_head_json, etc.). Re-run Songbird's
			// `backfill-posts-pages-catalog` script (songbird-service) to
			// upgrade them to the full payload. We surface a clear 404 here
			// rather than letting templates render an empty body.
			//
			// `content` is the canonical FLAT string form, but tolerate the
			// legacy `{ rendered }` object shape too so partially-migrated
			// buckets don't false-negative.
			const contentValue =
				typeof page_data?.content === "string"
					? page_data.content
					: typeof page_data?.content?.rendered === "string"
					? page_data.content.rendered
					: "";
			const hasFullContent = !!page_data && contentValue.trim().length > 0;
			if (!hasFullContent) {
				console.warn(
					`[widget page SSR] S3 payload for "${SLUG}" is the legacy index stub` +
						" — re-run Songbird's backfill-posts-pages-catalog script to backfill the full content."
				);
				throw createError({
					statusCode: 404,
					statusMessage: "Page content not available — S3 backfill required",
					fatal: true
				});
			}

			const response = normalizeS3PostToWpShape(page_data);

			const all_authors = await $fetch("/api/authors").catch((e) => {
				console.warn("[widget page SSR] authors fetch failed:", e?.message);
				return [];
			});

			payload.post = response;

			const fetched_author = response.author_info ||
				all_authors.find((a) => a.id === 1) || {
					id: response.author,
					name: "Timothy Sykes",
					profile_image:
						"https://content.timothysykes.com/wp-content/uploads/2024/08/timsykes.webp"
				};

			if (response.acf) {
				payload.acf = response.acf;
			}

			if (response.yoast_head_json) {
				payload.metadata = response.yoast_head_json;
			}

			// Tags + categories are pre-resolved on the S3 payload as full term
			// objects ({ id, name, slug, ... }) — no per-tag/per-category lookup.
			const tags = Array.isArray(response.tags) ? response.tags : [];
			const categories = Array.isArray(response.categories)
				? response.categories
				: [];

			// Featured media: S3 stores the resolved CDN URL on `featured_image`.
			// Templates read `post.featured_media` (string URL) and the synthetic
			// `_embedded["wp:featuredmedia"][0].source_url` left by the normalizer.
			const featured_media =
				response.featured_image ||
				response._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
				null;

			payload.post = {
				...payload.post,
				tags,
				categories,
				...(featured_media ? { featured_media } : {}),
				author: {
					id: fetched_author.id,
					name: fetched_author.name,
					avatar: fetched_author.profile_image
				}
			};

			payload.schema_json = schemaMaker(
				payload.post,
				$config.public.hostname,
				all_authors
			);

			return payload;
		} catch (error) {
			console.error("WIDGET PAGE ERROR during fetch:", error);

			// If it's already a createError with statusCode, re-throw it as-is
			if (error.statusCode) {
				throw error;
			}

			throw createError({
				statusCode: 500,
				statusMessage: "An error occurred during fetch",
				fatal: true
			});
		}
	}
);

if (!data.value || !data.value.post) {
	throw createError({
		statusCode: asyncError.value?.statusCode || 404,
		statusMessage: asyncError.value?.statusMessage || "Page Not Found",
		fatal: true
	});
}

watch(
	stockSymbols,
	async (newSymbols) => {
		if (process.client) {
			if (!newSymbols || newSymbols.length === 0) {
				stocksStore.stopTrackingSymbols();
				return;
			}

			const symbols = newSymbols
				.filter(Boolean)
				.map((symbol) => symbol.toUpperCase());

			await stocksStore.startTrackingSymbols(symbols);
		}
	},
	{
		deep: true,
		immediate: true
	}
);

const stocks_info = {
	stocks: computed(() => stocksStore.stocks)
};

const stocks = computed(() => stocks_info.stocks.value);

const stocks_ytd = computed(() => {
	const allStocks = stocks_info.stocks.value;
	if (!allStocks || typeof allStocks !== "object") return {};
	return Object.fromEntries(
		Object.entries(allStocks).filter(([, stock]) => stock?.NAME && stock?.SYMBOL)
	);
});

const { post, metadata, tags, schema_json, acf } = data.value;

const relatedPosts = ref([]);

const head_seo = computed(() => {
	if (metadata) {
		if (
			metadata.og_title &&
			(metadata.og_title.includes("[current_year]") ||
				metadata.og_title.includes("[current_month]"))
		) {
			metadata.og_title = metadata.og_title
				.replace(/\[current_year\]/g, String(new Date().getFullYear()))
				.replace(
					/\[current_month\]/g,
					new Date().toLocaleString("en-US", { month: "long" })
				);
		}

		const experiment = Object.entries(metadata);
		const filtered = experiment.filter(
			(item) =>
				![
					"title",
					"robots",
					"canonical",
					"schema_json",
					"WordPressAuthor_YoastHeadJson_Schema_"
				].includes(item[0])
		);
		for (let i = 0; i < filtered.length; i++) {
			if (typeof filtered[i][1] === "string") {
				filtered[i][1] = filtered[i][1].replace(
					/https:\/\/content\.timothysykes\.com/g,
					"https://www.timothysykes.com"
				);
			}
		}
		return filtered;
	}

	return null;
});

const mainImageFormated = computed(() => {
	// S3 stores `featured_image` as an already-proxied CDN URL, but
	// `normalizeS3PostToWpShape` strips that prefix into
	// `_embedded["wp:featuredmedia"][0].source_url`. applyCfImageProxy is
	// idempotent so either shape is safe.
	const sourceUrl =
		post?._embedded?.["wp:featuredmedia"]?.[0]?.source_url ||
		(typeof post?.featured_media === "string" ? post.featured_media : null);

	if (sourceUrl) {
		return {
			featuredImage: applyCfImageProxy(sourceUrl, {
				width: 740,
				quality: 90
			}),
			lowRes: applyCfImageProxy(sourceUrl, {
				width: 240,
				quality: 10
			})
		};
	}

	return null;
});

// Related "Keep Learning" recommendations on top-stocks / best-stocks /
// penny-stocks pages used to be fetched from WordPress REST filtered by the
// "More Penny Stocks To Watch" tag (28783). Since posts/pages are now served
// from S3 with no tag index, this feature is disabled until an S3-side tag
// index is added. The block was left in place so it's easy to re-enable once
// a replacement source exists.
//
// TODO: replace with an S3-based tag index lookup (e.g. ts-posts/tags/<id>.json)
//       or a dedicated /api/dictionary/related endpoint.
// eslint-disable-next-line require-await
const getRelatedPostsByTag = async () => {
	relatedPosts.value = [];
};

watch(widgetContent, (newContent) => {
	if (pennyStockingChapters.includes($route.fullPath)) {
		isPennyStockChapter.value = true;
	}

	if (newContent && process.client) {
		widgetContent.value.innerHTML = contentFormatter(post.content);
	}
	if (newContent && widgetContent.value && process.client) {
		const pseudoImage = widgetContent.value.querySelector("p");

		if (
			pseudoImage.children &&
			pseudoImage.children.length > 0 &&
			pseudoImage.children[0].nodeName === "IMG" &&
			pseudoImage.children[0].classList.contains("aligncenter")
		) {
			pseudoImage.remove();
		}
	}

	const read_more_button = document.querySelector("#read-more-blur");

	if (read_more_button) {
		read_more_button.onclick = () => {
			document.querySelector("#top-content").classList.add("active");
		};
	}

	if (process.client) {
		const toc_container = document.querySelector("#toc_container");

		if (toc_container) {
			const toc_list = document.querySelector(".toc_list");

			if (toc_list && toc_list.length === 0) {
				toc_container.remove();
			}

			const button = toc_container.querySelector("button");
			const anchor_links = toc_container.querySelectorAll("li a");

			anchor_links.forEach((link) =>
				link.addEventListener("click", onClickOnAnchorScrollToTitle)
			);

			button.addEventListener("click", () => {
				const toc = toc_container.querySelector(".toc_list");

				if (toc.classList.contains("active")) {
					button.classList.remove("active");
					toc.classList.remove("active");
				} else {
					button.classList.add("active");
					toc.classList.add("active");
				}
			});
		}

		// if (store.contentSidebar.data && store.contentSidebar.show) {
		// 	const container = document.querySelector(".blog-container");
		// 	if (container) {
		// 		container.classList.add("has-sidebar");
		// 	}
		// }

		if (newContent) {
			store.contentDoneLoading();
		}
	}
});

const pennyStockingFormatter = (content) => {
	const parsedContent = widgetContentFormatter(content);

	const $ = load(parsedContent, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	$("img").each((_index, imgElement) => {
		let dataSrc = imgElement.attribs["data-src"];

		if (dataSrc) {
			const proxy_srcset =
				"https://content.timothysykes.com/cdn-cgi/image/quality=80,format=webp,width=172/";

			dataSrc = proxy_srcset + dataSrc;

			$(imgElement).attr("src", dataSrc);
		}
	});

	$("a").each((_index, el) => {
		const regex = new RegExp(
			"\\b" + $config.public.hostname + "(?!\\.(jpg|jpeg|png|webp|pdf|gif)\\b)",
			"gi"
		);

		if (el.attribs.href && el.attribs.href.match(regex)) {
			const extracted_slug = el.attribs.href.split(".com/")[1];
			$(el).attr("href", $config.public.current_hostname + extracted_slug);
		}

		if (el.attribs.href && !el.attribs.href.includes("#")) {
			$(el).addClass("external-link");
		}
	});

	return $.html();
};
const lastStockParseRoute = ref(null);
const contentFormatter = (content) => {
	if ($route.path !== lastStockParseRoute.value) {
		stockSymbols.value = [];
		lastStockParseRoute.value = $route.path;
	}

	const parsedContent = widgetContentFormatter(content);

	const $ = load(parsedContent, {
		decodeEntities: false, // Prevent HTML entity decoding
		xmlMode: false // Do not treat input as XML
	});

	$("script").each((_index, scriptElement) => {
		if (
			$(scriptElement).attr("src") === "https://platform.twitter.com/widgets.js"
		) {
			$(scriptElement).remove();

			hasTwitterEmbedd.value = true;
		}

		if ($(scriptElement).attr("async")) {
			// Remove the async attribute
			$(scriptElement).removeAttr("async");

			// Add the defer attribute
			$(scriptElement).attr("defer", "");
		}
	});

	// let regex = /\[(NASDAQ|NYSE):\s*([A-Z]+)\)/;

	processHeadingsForReferencedStocks($, (stockSymbol) => {
		if (!stockSymbols.value.includes(stockSymbol)) {
			stockSymbols.value.push(stockSymbol);
		}
	});
	$("p").each((_, pElement) => {
		const text = $(pElement).text();

		const matches = text.match(STOCK_TICKER_REGEX);

		if (matches && matches[1]) {
			const stockSymbol = extractStockSymbol(text);
			if (!stockSymbols.value.includes(stockSymbol)) {
				stockSymbols.value.push(stockSymbol);
			}
		}
	});

	$("iframe").each((_index, iframeEl) => {
		if (!$(iframeEl).attr("loading")) {
			$(iframeEl).attr("loading", "lazy");
		}
		if (!$(iframeEl).attr("title")) {
			$(iframeEl).attr("title", $(iframeEl).attr("src"));
		}

		if (iframeEl.attribs.src.includes("displayprices=Candlestick")) {
			$(iframeEl).addClass("stockdio-candlestick");
		}

		if (iframeEl.attribs.class === "stockdio_quotes") {
			$(iframeEl).wrap('<div class="stockdio-wrapper"></div>');
		}
	});

	$("#toc_container .toc_title").each((_index, toc_title) => {
		// const title = toc.querySelector('.toc_title');
		const button = $(`<button aria-label="Toggle Table of Contents">
      <span class="expand"> <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/><path d="M12.5 7.5V17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/><path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/></svg></span>
      <span class="collapse">
        <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12.5" cy="12.5" r="11.5" fill="#3094FB" stroke="#FCFCFD" stroke-width="2"/>
          <path d="M7.5 12.5H17.5" stroke="white" stroke-width="1.5" stroke-linecap="round"/>
        </svg>

        </span>
      </button>`);

		$(toc_title).append(button);
	});

	$("img").each((_index, imgElement) => {
		const content = "https://content.timothysykes.com/";
		let src = imgElement.attribs.src;
		let srcset = imgElement.attribs.srcset;

		src = src.replace($config.public.hostname, content);

		if (srcset) {
			srcset = rewriteSrcsetWithCfProxy(srcset);
		}

		const proxiedSrc = applyCfImageProxy(src);

		$(imgElement).attr("src", proxiedSrc);
		$(imgElement).attr("data-src", proxiedSrc);
		$(imgElement).attr("srcset", srcset);
	});

	$("a").each((_index, el) => {
		const regex = /^https:\/\/.*\.(?!jpg$|jpeg$|png$|webp$|pdf$|gif$).*$/;
		if (regex.test(el.attribs.href)) {
			mentioned_above_urls.value.push(el.attribs.href);
		}
		if (el.attribs.href.includes("https://content.timothysykes.com")) {
			el.attribs.href = el.attribs.href.replace(
				"https://content.timothysykes.com",
				$config.public.current_hostname.slice(0, -1)
			);
		}

		if (
			el.attribs.href &&
			!el.attribs.href.includes("#") &&
			$(el).find("img").length === 0
		) {
			$(el).addClass("external-link");
		}
	});

	$("#top-content").append(
		'<div class="read-more-container"><button id="read-more-blur" @click="readMoreContent">Read more</button>'
	);

	$("div#bottom_content").each(function (index) {
		$(this).addClass(`content${index + 1}`);
	});

	$(".content2").addClass("hidden-element");

	// Create faq component if needed
	if (widgetContent.value) {
		const content = widgetContent.value.innerHTML;
		const $ = load(content, {
			decodeEntities: false,
			xmlMode: false
		});

		// Takes and filter the title
		const h2WithFAQ = $("h2 > span[id$='_FAQs']").closest("h2");

		if (h2WithFAQ.length) {
			let h2Html = h2WithFAQ.text().trim();
			h2Html = h2Html.replace(/FAQs$/, "");
			title = h2Html;
		}

		// For cases where the faq isn't inside the accordion div
		const lastBottomContent = $("div#bottom_content").last();
		const contentHtml = lastBottomContent.html();
		const regex = /<h3><span.*?>(FAQ|faq)(.*?)<\/span>[\s\S]*?<p>(.*?)<\/p>/gim;

		let match;
		while ((match = regex.exec(contentHtml))) {
			const title = match[1].trim();
			const answer = match[2].trim();

			const faqItem = {
				title,
				answer
			};

			faqItems.value.push(faqItem);
		}

		// For cases where the faq is inside the accordion
		if (faqItems.value.length === 0) {
			const h3WithFAQ = $("h3 > span[id$='_FAQs']").closest("h3");

			// Takes and filter the title
			if (h3WithFAQ.length) {
				let h3Html = h3WithFAQ.text().trim();
				h3Html = h3Html.replace(/FAQs$/, "");
				title = h3Html;
			}

			const buttons = $("button.accordion");
			const answers = $("p.faq_content");

			buttons.each((index, button) => {
				const buttonTitle = $(button).text().trim();
				const answer = $(answers[index]).text().trim();

				const faqItem = {
					title: buttonTitle,
					answer
				};

				faqItems.value.push(faqItem);
			});
		}
	}
	$("table.dynamic-table").each((_, tableElement) => {
		const $table = $(tableElement);
		const $tbody = $table.find("tbody");
		const $tr = $tbody.find("tr");

		$tr.each((_, trElement) => {
			const $td = $(trElement).find("td");
			const regex = /\b(NASDAQ|NYSE|AMEX|OTCPK)\b\s*:\s*([A-Z]+)\b/;
			let stockSymbol = null;

			// Search through all columns to find the ticker
			for (let i = 0; i < $td.length; i++) {
				const text = $td.eq(i).text().trim();
				const matches = text.match(regex);

				if (matches && matches[2]) {
					stockSymbol = matches[2].replace(/\s/g, "").toUpperCase();
					break;
				}
			}

			if (stockSymbol) {
				// Track the ticker so YTD data gets fetched for table-only symbols
				if (!stockSymbols.value.includes(stockSymbol)) {
					stockSymbols.value.push(stockSymbol);
				}

				// Set the data-stock-ticker attribute on the Performance (YTD) column (usually the 3rd column, index 2)
				if ($td.length > 2) {
					$td.eq(2).attr("data-stock-ticker", stockSymbol);
				}
			}
		});
	});

	return $.html();
};

if (!store.contentSidebar.postTags) {
	if (tags) store.setPostsTags(tags);
} else {
	const matchingTags = [...store.contentSidebar.postTags].filter((tag) =>
		[...tags].includes(tag)
	);

	if (matchingTags.length === 0) {
		store.setPostsTags(tags);
	}
}

const pagePost = computed(() => data.value?.post ?? null);

watch(
	[() => $route.path, () => collectSidebarTagIds(pagePost.value)],
	async ([, ids]) => {
		if (!process.client) return;

		if (!ids?.length) {
			if (store.contentSidebar.show) {
				store.toggleContentSidebarVisibility(false);
			}
			return;
		}

		await useSidebarSorter(ids);
	},
	{ immediate: true }
);

watch(hasTwitterEmbedd, async (newContent) => {
	if (process.client && newContent) {
		await setTimeout(addTwitterEmbedd, 1000);
	}
});

watch(
	stocks,
	(newData, _) => {
		if (newData && process.client) {
			Object.entries(newData).forEach(([_, stock]) => {
				const element = document.querySelector(
					`[data-stock-ticker="${stock.SYMBOL}"]`
				);

				if (element && stock.YTD_PCT != null) {
					element.setAttribute(
						"class",
						stock.YTD_PCT > 0 ? "symbol-status up" : "symbol-status down"
					);
					element.innerHTML = `${stock.YTD_PCT > 0 ? "+" : ""}${stock.YTD_PCT}%`;
				}
			});
		}
	},
	{ deep: true }
);

onMounted(async () => {
	if (process.client) {
		if (data.value.redirected && process.client) {
			return;
		}

		const beyondwords_podcast_id = post.value?.meta?.beyondwords_podcast_id;
		if (beyondwords_podcast_id) {
			const beyondwords_res = await fetchBeyondWords({
				podcastId: beyondwords_podcast_id
			});

			if (beyondwords_res) {
				beyondwords.value = beyondwords_res;
			}
		}

		const toc_container = document.querySelector("#toc_container");

		if (toc_container) {
			const button = toc_container.querySelector("button");
			const anchor_links = toc_container.querySelectorAll("li a");

			anchor_links.forEach((link) =>
				link.addEventListener("click", onClickOnAnchorScrollToTitle)
			);

			const toc = toc_container.querySelector(".toc_list");

			if (toc.childElementCount === 0) {
				document.querySelector("#toc_container").remove();
			}

			if (button) {
				button.addEventListener("click", () => {
					const toc = toc_container.querySelector(".toc_list");

					if (toc.classList.contains("active")) {
						button.classList.remove("active");
						toc.classList.remove("active");
					} else {
						button.classList.add("active");
						toc.classList.add("active");
					}
				});
			}
		}

		let counter = 0;

		const hostname = "https://content.timothysykes.com/";

		// Wait for content to load before parsing YouTube embeds
		// 250ms intervals, up to 10 retries (2.5 seconds total) for mobile devices
		const interval = setInterval(() => {
			counter += 1;
			const result = youtubeEmbedParser(hostname);
			if (result === "close" || counter >= 10) {
				clearInterval(interval);
			}
		}, 250);

		showAds.value = window.adsAreWithUs;

		getRelatedPostsByTag();

		await checkForCookieAndSwipeUp();
	}

	if (pennyStockingChapters.includes($route.fullPath)) {
		isPennyStockChapter.value = true;
	}

	if (post) {
		store.manualToggleLoading(false);
	}
});

const extractTextWithTripleBrackets = (content) => {
	const regex = /\[\[\[([^]+?)\]\]\]/g;
	const matches = [];
	let match;

	while ((match = regex.exec(content)) !== null) {
		matches.push(match[0]); // Push the entire match, including [[[ and ]]]
	}

	return matches;
};

const extractShortcode = (content) => {
	const regex = /\[screeners-widget (.*?)\]/g;
	let match;

	// eslint-disable-next-line no-unreachable-loop
	while ((match = regex.exec(content)) !== null) {
		let shortcodeContent = match[1];

		shortcodeContent = decodeHTMLEntities(shortcodeContent);
		shortcodeContent = shortcodeContent
			.replace(/″/g, '"')
			.replace(/'/g, '"')
			.replace(/”/g, '"')
			.replace(/“/g, '"')
			.replace(/’/g, '"')
			.replace(/″/g, ":")
			.replace(/=/g, ":")
			.split(" ");

		const params_value = JSON.parse(
			shortcodeContent[1].split(`params:"`)[1].slice(0, -1)
		);

		const arr = Object.keys(params_value).map((key) => {
			return [key, params_value[key]];
		});

		return {
			widgetName: shortcodeContent[0].split(':"')[1].slice(0, -1),
			params: arr
		};
	}

	return null;
};
const decodeHTMLEntities = (str) => {
	const entities = {
		"&#8220;": "“",
		"&#8221;": "”",
		"&#8243;": "″",
		"&#8217;": "’"
	};

	return str.replace(/&#[0-9]+;/g, (match) => entities[match] || match);
};

const routeForcing = (url) => {
	if (process.client) {
		if (
			url === "/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-1/"
		) {
			return window.location.replace("/penny-stocking-chapter-01/");
		}
		window.location.replace(url);
	}
};

const widgetContentFormatter = (content) => {
	const newParser = extractShortcode(content);

	let extractedTextsWithBrackets;
	let params;
	let name;
	let sections;

	if (!newParser) {
		extractedTextsWithBrackets = extractTextWithTripleBrackets(content);

		if (extractedTextsWithBrackets.length === 0) {
			return content;
		}

		const widgetPattern = /\[\[\[(.*?)\]\]\]/g;
		const tripleBracketContentRegex = /\[\[\[([\s\S]+?)\]\]\]/;
		sections = content.split(widgetPattern);

		let obj = decodeHTMLEntities(extractedTextsWithBrackets[0]);

		if (!obj) {
			return content;
		}
		obj = obj.match(tripleBracketContentRegex)[1];

		obj = JSON.parse(
			obj
				.replace(/“/g, '"')
				.replace(/”/g, '"')
				.replace(/″/g, '"')
				.replace(/’/g, "'")
		);

		params = Object.keys(obj.params)
			.map((key) => key + "=" + obj.params[key])
			.join("&");
	} else {
		name = newParser.widgetName;
		params = newParser.params.map(([key, value]) => `${key}=${value}`).join("&");

		sections = content.split(/\[screeners-widget (.*?)\]/g);
	}

	const iframe = `<iframe id="${name}"
                     width="786"
                     height="610"
                     title="Screener"
                     src="https://scn-wdgt-01-dev.timothysykes.com/?${params}"></iframe>`;

	return sections[0] + iframe + sections[2];
};

useHead(() => {
	const link = [
		{
			rel: "canonical",
			href: `${current_hostname?.slice(0, -1) || ""}${$route.path}`
		}
	];

	const title = post?.yoast_head_json?.og_title || post.title;

	const obj = {
		title,
		link
	};
	if (schema_json) {
		return {
			...obj,
			script: [
				{
					type: "application/ld+json",
					class: "saswp-schema-markup-output",
					innerHTML: JSON.stringify(schema_json)
				}
			]
		};
	}
	return {
		...obj
	};
});

const showReferencedStocksSidebar = computed(() => {
	return (
		stockSymbols.value.length > 0 &&
		Object.values(stocks.value).some((stock) => stock?.NAME && stock?.SYMBOL)
	);
});
</script>

<template>
	<Head v-if="head_seo">
		<template
			v-for="(tag, i) in head_seo"
			:key="i"
		>
			<Meta
				v-if="tag[0].startsWith('og_')"
				:property="'og:' + tag[0].slice(3)"
				:content="tag[1][0].url || tag[1]"
			/>

			<Meta
				v-else-if="tag[0].startsWith('article')"
				:property="'article:' + tag[0].slice(8)"
				:content="tag[1]"
			/>
			<Meta
				v-else-if="tag[0] !== 'twitter_misc' && tag[0].startsWith('twitter')"
				:name="'twitter:' + tag[0].slice(8)"
				:content="tag[1]"
			/>

			<template v-else-if="tag[0] === 'twitter_misc'">
				<Meta
					:name="'twitter:label1'"
					:content="Object.keys(tag[1])[0]"
				/>
				<Meta
					:name="'twitter:data2'"
					:content="Object.values(tag[1])[0]"
				/>
				<Meta
					:name="'twitter:label2'"
					:content="Object.keys(tag[1])[1]"
				/>
				<Meta
					:name="'twitter:data1'"
					:content="Object.values(tag[1])[1]"
				/>
			</template>

			<Meta
				v-else-if="tag[0] != 'og_image'"
				:name="tag[0]"
				:content="tag[1]"
			/>
			<template v-else>
				<Meta
					:property="tag[0].split('_').join(':') + ':type'"
					:content="tag[1][0].type"
				/>
				<Meta
					:property="tag[0].split('_').join(':')"
					:content="tag[1][0].url"
				/>
				<Meta
					:property="tag[0].split('_').join(':') + ':width'"
					:content="tag[1][0].width"
				/>
				<Meta
					:property="tag[0].split('_').join(':') + ':height'"
					:content="tag[1][0].height"
				/>
			</template>
		</template>
	</Head>

	<BlogContainer id="blog-page-container">
		<template v-if="store.isLoading">
			<Loading />
			<section class="faux-loading" />
		</template>

		<template
			v-if="!store.isLoading && post"
			#main
		>
			<Heading
				:text="post.title"
				tag="h1"
			/>
			<AuthorshipBlock
				v-if="post.author"
				class="mb-[12px]"
				v-bind="authorship"
				:author="{
					avatar: post.author.avatar,
					name: `${post.author.name ? post.author.name : 'Timothy Sykes'}`
				}"
			/>

			<DateReadingTime
				:date="
					metadata.article_modified_time
						? metadata.article_modified_time
						: post.dateGmt
				"
				:reading-time="calculateReadingTime(post.content)"
			/>

			<section
				v-if="mainImageFormated"
				class="featured-image mb-[24px]"
			>
				<img
					:src="mainImageFormated.featuredImage"
					:alt="`${post.title} Thumbnail`"
					class="w-full"
					width="740"
					height="532"
				/>
			</section>
			<section
				v-else
				class="featured-image mb-[24px]"
			>
				<img
					src="/images/default_blog_image.webp"
					:alt="`${post.title} Thumbnail`"
					class="w-full"
					width="740"
					height="532"
				/>
			</section>

			<SharePost :type="'primary'" />

			<LazyBeyondWordsIframe
				v-if="beyondwords && beyondwords.id && beyondwords.state === 'processed'"
				:beyondwords="beyondwords.id"
			/>
			<ClientOnly v-if="stockSymbols.length > 0">
				<ReferencedStocks :stocks="stocks" />
			</ClientOnly>

			<section
				ref="widgetContent"
				class="content"
				v-html="post.content"
			/>

			<AizenEndOfArticleAd />
			<section
				v-if="isPennyStockChapter && acf"
				class="content acf-content"
			>
				<template v-if="acf.section && acf.section.length">
					<template
						v-for="(content, i) in acf.section"
						:key="i"
					>
						<h2 v-html="content.section_title" />
						<div
							class="mb-[32px]"
							v-html="pennyStockingFormatter(content.section_detail)"
						/>
					</template>
				</template>
			</section>

			<FAQ
				v-if="faqItems && faqItems.length > 0"
				:faqs="faqItems"
				:processed-title="title"
			/>

			<Divider />

			<RatingsComponent
				id="ratings-component"
				:post-id="post.id"
			/>

			<template v-if="relatedPosts && relatedPosts.length > 0">
				<Divider />

				<section id="related-posts">
					<Heading
						tag="h4"
						:text="`Keep learning`"
						class="mb-[16px] font-semibold"
					/>

					<div class="flex flex-col gap-[24px]">
						<template
							v-for="(related_post, index) in relatedPosts"
							:key="related_post.uri"
						>
							<AizenRelatedPostAd v-if="index === 1" />

							<RelatedPostCard
								:image="
									related_post.yoast_head_json &&
									related_post.yoast_head_json.og_image &&
									related_post.yoast_head_json.og_image.length > 0 &&
									related_post.yoast_head_json.og_image[0].url
										? related_post.yoast_head_json.og_image[0].url
										: ''
								"
								:title="related_post.title"
								:text="related_post.excerpt"
								:url="related_post.link"
							/>
						</template>
					</div>
				</section>
			</template>

			<template v-if="isPennyStockChapter">
				<div class="flex justify-between gap-[12px] mt-8">
					<Button
						v-if="acf.chapter_previous_text"
						class="ml-0 ps-button"
						appearance="secondary"
						:label="acf.chapter_previous_text"
						:on-click="
							() => routeForcing(acf.chapter_previous_link.split('.com')[1] + '/')
						"
					/>
					<Button
						v-if="acf.chapter_next_title"
						class="mr-0 ps-button"
						appearance="secondary"
						:is-finished="acf.chapter_next_title === 'Finished'"
						:label="
							$route.fullPath ===
								'/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-7/' &&
							acf.chapter_next_title === 'Take me to <br>chapter seven'
								? 'Take me to <br>chapter eight'
								: acf.chapter_next_title
						"
						:on-click="
							() =>
								routeForcing(
									$route.fullPath ===
										'/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-7/' &&
										acf.chapter_next_link.split('.com')[1] ===
											`/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-7`
										? '/penny-stocking-101-how-to-get-started-with-penny-stocks-chapter-8/'
										: acf.chapter_next_link.split('.com')[1] + '/'
								)
						"
					/>
				</div>

				<Divider />

				<Comments
					id="comments"
					:comments="post.comments"
				/>
				<Divider />
			</template>

			<section
				v-if="mentioned_above_urls && mentioned_above_urls.length > 0"
				id="mentioned-above-post"
			>
				<Heading
					tag="h4"
					:text="`Mentioned Above`"
					class="mb-[16px] font-semibold"
				/>

				<div class="flex flex-col gap-[32px] md:gap-[24px]">
					<template
						:key="i"
						v-for="(url, i) in mentioned_above_urls.splice(0, 4)"
					>
						<NuxtLink
							class="text-base text-primary-500 underline"
							target="_blank"
							:to="url"
						>
							{{ url }}
						</NuxtLink>

						<div
							v-if="i === 1"
							class="lg:sticky lg:top-[92px]"
						>
							<AizenRelatedPostAd />
						</div>
					</template>
				</div>
			</section>
		</template>
		<template
			v-if="!store.isLoading && post"
			#aside
		>
			<Divider class="lg:hidden" />
			<TimothyBioPreview class="mb-[32px]" />

			<div class="lg:sticky lg:top-[92px]">
				<AizenSidebarAd />

				<ClientOnly>
					<ReferencedStocksSidebar
						id="referenced-stocks-sidebar"
						:class="{ 'has-ad': !!aizenSidebarAd }"
						v-if="showReferencedStocksSidebar && Object.keys(stocks_ytd).length > 0"
						:stocks="stocks_ytd"
					/>
				</ClientOnly>
			</div>
		</template>
	</BlogContainer>
</template>

<style lang="scss">
@import "@assets/utils.scss";

#widget {
	overflow-x: auto;
}

#top-content {
	height: 240px;
	overflow-y: hidden;
	overflow-x: hidden;
	transition: all 400ms ease-in;
	position: relative;
}
#top-content.active {
	height: 100%;
}
#top-content.active .read-more-container {
	display: none;
}

.read-more-container {
	position: absolute;
	bottom: 0;
	display: flex;
	justify-content: center;
	text-align: center;
	width: 100%;
	height: 160px;
	padding: 32px 16px;
	align-items: flex-end;
	backdrop-filter: blur(2px);
	margin: auto;
	left: 0;
	right: 0;
	@include breakpoint(lg) {
		width: 120%;
	}
}

.read-more-container:before {
	position: absolute;
	height: 100%;
	bottom: -6px;
	left: 0;
	right: 0;
	filter: blur(2px);
	content: " ";
	z-index: -1;
	background: linear-gradient(
		0deg,
		#eff6ff 20%,
		hsl(213.8, 100%, 96.9%) 50%,
		hsla(0, 0%, 100%, 0)
	);
	width: 120%;
	margin: 0 -32px;
	opacity: 0.95;
}

#read-more-blur {
	position: absolute;
	left: 0;
	right: 0;
	margin: auto;
	max-width: 220px;

	border-radius: 8px;
	background: #3094fb;
	padding: 16px;
	font-family: Rubik;
	font-size: 18px;
	font-style: normal;
	font-weight: 700;
	line-height: 150%;
	/* 27px */
	letter-spacing: 0.18px;
	color: white;
}

@media (min-width: 768px) {
	#read-more-blur {
		left: -17%;
	}
}

.content {
	margin-bottom: 32px;
}

.content figure {
	width: auto !important;
}

.content p {
	color: $neutral-800;

	/* Body/Xlarge */
	font-family: Rubik;
	font-size: 18px;
	font-style: normal;
	font-weight: 400;
	line-height: 150%;
	/* 33px */
	margin-bottom: 32px;

	&:last-child {
		margin-bottom: 0;
	}
}

.content h3 {
	color: $neutral-800;
	font-family: Fira Sans;
	font-size: 22px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%;
	/* 38.4px */
	margin-bottom: 24px;

	@include breakpoint(lg) {
		font-size: 32px;
		margin-bottom: 32px;
	}
}

.content figure {
	margin-bottom: 32px;
}

.content figcaption {
	color: $neutral-400;

	/* Body/Small */
	font-family: Rubik;
	font-size: 16px;
	font-style: normal;
	font-weight: 400;
	line-height: 150%;
	/* 24px */
	padding: 10px;
}

.content h2 {
	color: $neutral-800;

	/* Heading/H2 */
	font-family: Fira Sans;
	font-size: 28px;
	font-style: normal;
	font-weight: 700;
	line-height: 120%;
	margin-bottom: 32px;

	/* 45.6px */
	letter-spacing: -0.76px;

	@include breakpoint(lg) {
		font-size: 38px;
	}
}

#scn-wdgt-01-dev {
	width: 100%;
}

.content ul {
	list-style-type: square;
	padding-left: 16px;
}

.content ul li::marker {
	color: $primary-500;
}

.content ul li {
	color: $neutral-800;

	/* Body/Regular */
	font-family: Rubik;
	font-size: 18px;
	font-style: normal;
	font-weight: 400;
	line-height: 160%;
	padding-left: 8px;
	margin-bottom: 32px;

	/* 29.6px */
}

.content a {
	color: $primary-500;
}

.hidden-element {
	display: none;
}

@media (min-width: 1280px) and (max-width: 1764px) {
	.blog-container.has-sidebar {
		margin-left: auto;
		max-width: calc(100% - 268px);
		padding: 0 16px;
		gap: 24px;
	}
}

@media (min-width: 1280px) and (max-width: 1764px) {
	.blog-container.has-sidebar {
		margin-left: auto;
		max-width: calc(100% - 268px);
		padding: 0 16px;
		gap: 24px;
	}
}

#toc_container {
	max-width: 640px;

	.toc_title {
		color: var(--base-white, #fff);
		font-family: Fira Sans;
		font-size: 20px;
		font-style: normal;
		font-weight: 700;
		line-height: 120%;
		margin-bottom: 0;
		display: inline-flex;
		gap: 12px;

		button {
			.expand {
				display: block;
			}

			.collapse {
				display: none;
			}

			&.active {
				.expand {
					display: none;
				}

				.collapse {
					display: block;
					visibility: visible;
				}
			}
		}
	}

	.toc_list {
		height: 0;
		overflow: hidden;
		transition: all 200ms ease-in-out;
		opacity: 0;

		&.active {
			height: 100%;
			margin-top: 16px;
			opacity: 1;
		}
	}

	padding: 20px 16px;
	background-color: $primary-900;
	border-radius: 8px;

	margin-bottom: 32px;

	@include breakpoint(lg) {
		padding: 32px;
	}

	ul {
		padding-left: 0;
	}

	ul li {
		text-decoration: none;
		font-weight: 600;
		margin-bottom: 8px;

		@include breakpoint(md) {
			margin-bottom: 12px;
		}
		&:last-child {
			margin-bottom: 0;
		}

		ul {
			margin-top: 12px;

			padding-left: 24px;

			li {
				font-weight: 400;
			}
		}
	}

	ul li::marker {
		content: "";
		display: none;
	}

	ul li a {
		color: white;
		font-family: Rubik;
		font-size: 16px;
		font-style: normal;
		line-height: 160%;
		margin-bottom: 8px;

		@include breakpoint(md) {
			font-size: 18px;
			margin-bottom: 12px;
		}

		&:hover {
			text-decoration: underline;
		}

		/* 29.6px */
	}
}

.acf-contentul li {
	list-style: none;
	position: relative;
	padding: 0 0 0 33px;
}

.acf-content li {
	margin-bottom: 0.8333333333em;
}

.acf-content ol,
.acf-content p,
.acf-content ul {
	margin-bottom: 1.6666666667em;
}

.acf-content .alignleft {
	float: left;
	margin: 0.4166666667em 1.6666666667em 1.6666666667em 0;
}

.acf-content ul li:before {
	content: "";
	display: block;
	width: 12px;
	height: 12px;
	background: #3fa9f5;
	position: absolute;
	top: 0.8333333333em;
	left: 0;
	margin-top: -4px;
}

.acf-content em {
	font-style: italic;
}
.acf-content br {
	display: block;
	height: 20px;
	content: "";
}

@media (max-width: 700px) {
	.ps-button {
		padding-left: 8px !important;
		padding-right: 8px !important;
		font-size: 14px !important;
	}
}

.external-link {
	display: revert-layer;
	align-items: center;
	position: relative;
	&::after {
		content: "";
		background-image: url("/images/icons/external-link.svg");
		width: 14px;
		height: 14px;
		display: inline-block;
		background-size: contain;
		margin-left: 2px;
	}

	&:hover {
		background-color: #eab308;
		color: #1e3a8a;
		&::after {
			background-image: url("/images/icons/external-link-hover.svg");
		}
	}
}

.external-icon {
	width: 18px;
	height: 18px;
}

.referenced-symbols:not(#blog-page-container) {
	background-color: #f4f4f4;
}

#blog-page-container .referenced-symbols {
	background-color: #ffffff;
}
</style>
